import { useState, useEffect, useCallback } from "react";
import { locationApi } from "../services/api";

/**
 * Extract data from API response (handles both casing conventions)
 */
const extractData = (response) => {
  if (!response) return { success: false, data: null };
  const success = response.success ?? response.Success ?? false;
  const data = response.data ?? response.Data ?? null;
  const message = response.message ?? response.Message ?? null;
  return { success, data, message };
};

/**
 * Hook for managing locations data (admin functionality)
 */
export const useLocations = (options = {}) => {
  const { autoFetch = false, tenantId = null, activeOnly = null } = options;

  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchLocations = useCallback(
    async (params = {}) => {
      setLoading(true);
      setError(null);

      try {
        const queryParams = {
          ...params,
          ...(tenantId && { tenantId }),
          ...(activeOnly !== null && { activeOnly }),
        };
        const response = await locationApi.getAll(queryParams);
        const { success, data, message } = extractData(response);

        if (success) {
          setLocations(data || []);
          return data || [];
        } else {
          throw new Error(message || "Failed to fetch locations");
        }
      } catch (err) {
        const errorMessage = err.message || "Failed to fetch locations";
        setError(errorMessage);
        console.error("Error fetching locations:", err);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [tenantId, activeOnly],
  );

  const getLocation = useCallback(async (id) => {
    try {
      const response = await locationApi.getById(id);
      const { success, data, message } = extractData(response);

      if (success) {
        return data;
      } else {
        throw new Error(message || "Failed to fetch location");
      }
    } catch (err) {
      console.error("Error fetching location:", err);
      throw err;
    }
  }, []);

  const createLocation = useCallback(async (data, targetTenantId = null) => {
    setLoading(true);
    try {
      const response = await locationApi.create(data, targetTenantId);
      const { success, data: created, message } = extractData(response);

      if (success) {
        setLocations((prev) => [...prev, created]);
        return created;
      } else {
        throw new Error(message || "Failed to create location");
      }
    } catch (err) {
      console.error("Error creating location:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateLocation = useCallback(async (id, data) => {
    setLoading(true);
    try {
      const response = await locationApi.update(id, data);
      const { success, data: updated, message } = extractData(response);

      if (success) {
        setLocations((prev) => prev.map((l) => (l.id === id ? updated : l)));
        return updated;
      } else {
        throw new Error(message || "Failed to update location");
      }
    } catch (err) {
      console.error("Error updating location:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteLocation = useCallback(async (id) => {
    setLoading(true);
    try {
      const response = await locationApi.delete(id);
      const { success, message } = extractData(response);

      if (success) {
        setLocations((prev) => prev.filter((l) => l.id !== id));
        return true;
      } else {
        throw new Error(message || "Failed to delete location");
      }
    } catch (err) {
      console.error("Error deleting location:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deactivateLocation = useCallback(async (id) => {
    setLoading(true);
    try {
      const response = await locationApi.deactivate(id);
      const { success, message } = extractData(response);

      if (success) {
        setLocations((prev) =>
          prev.map((l) => (l.id === id ? { ...l, isActive: false } : l)),
        );
        return true;
      } else {
        throw new Error(message || "Failed to deactivate location");
      }
    } catch (err) {
      console.error("Error deactivating location:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-fetch on mount if enabled
  useEffect(() => {
    if (autoFetch) {
      fetchLocations();
    }
  }, [autoFetch, fetchLocations]);

  return {
    locations,
    loading,
    error,
    fetchLocations,
    getLocation,
    createLocation,
    updateLocation,
    deleteLocation,
    deactivateLocation,
    refresh: fetchLocations,
  };
};

/**
 * Hook for managing user location access (admin functionality)
 */
export const useLocationAccess = (locationId = null) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchLocationUsers = useCallback(
    async (locId = locationId) => {
      if (!locId) return [];

      setLoading(true);
      setError(null);

      try {
        const response = await locationApi.getLocationUsers(locId);
        const { success, data, message } = extractData(response);

        if (success) {
          setUsers(data || []);
          return data || [];
        } else {
          throw new Error(message || "Failed to fetch location users");
        }
      } catch (err) {
        const errorMessage = err.message || "Failed to fetch location users";
        setError(errorMessage);
        console.error("Error fetching location users:", err);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [locationId],
  );

  const grantAccess = useCallback(async (data) => {
    try {
      const response = await locationApi.grantAccess(data);
      const { success, data: access, message } = extractData(response);

      if (success) {
        setUsers((prev) => [...prev, access]);
        return access;
      } else {
        throw new Error(message || "Failed to grant access");
      }
    } catch (err) {
      console.error("Error granting access:", err);
      throw err;
    }
  }, []);

  const updateAccess = useCallback(async (accessId, data) => {
    try {
      const response = await locationApi.updateAccess(accessId, data);
      const { success, data: updated, message } = extractData(response);

      if (success) {
        setUsers((prev) => prev.map((u) => (u.id === accessId ? updated : u)));
        return updated;
      } else {
        throw new Error(message || "Failed to update access");
      }
    } catch (err) {
      console.error("Error updating access:", err);
      throw err;
    }
  }, []);

  const revokeAccess = useCallback(async (accessId) => {
    try {
      const response = await locationApi.revokeAccess(accessId);
      const { success, message } = extractData(response);

      if (success) {
        setUsers((prev) => prev.filter((u) => u.id !== accessId));
        return true;
      } else {
        throw new Error(message || "Failed to revoke access");
      }
    } catch (err) {
      console.error("Error revoking access:", err);
      throw err;
    }
  }, []);

  // Auto-fetch when locationId changes
  useEffect(() => {
    if (locationId) {
      fetchLocationUsers(locationId);
    }
  }, [locationId, fetchLocationUsers]);

  return {
    users,
    loading,
    error,
    fetchLocationUsers,
    grantAccess,
    updateAccess,
    revokeAccess,
    refresh: () => fetchLocationUsers(locationId),
  };
};

export default useLocations;
